import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useCreateTemplate } from './hooks/useCreateTemplate';
import { clearMedia } from '../../store/mediaSlice';
import { resetWhatsappState, resetAIGeneration } from '../../store/whatsappSlice';
import TemplatePageHeader from './components/TemplatePageHeader';
import TemplateDetailsForm from './components/TemplateDetailsForm';
import AuthenticationTemplateForm from './components/AuthenticationTemplateForm';
import RegularTemplateForm from './components/RegularTemplateForm';
import AIGeneratorCard from './components/AIGeneratorCard';
import { TemplatePreview } from '../../components/whatsapp/templates/TemplatePreview';
import { showAITemplateGenerator } from '../../utils/featureFlags';
import { whatsappPageShell, waShell } from './utils/whatsappPageShell';
import { usePageTitle } from '../../hooks/usePageTitle';

export default function CreateTemplate() {
  usePageTitle('New Template');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    formData,
    setFormData,
    setMultipleFormData,
    validationErrors,
    headerVars,
    bodyVars,
    sampleValues,
    sampleErrors,
    handleSampleChange,
    sampleValidationMessage,
    localHeaderError,
    localBodyError,
    handleHeaderTextChange,
    loading,
    submit,
    success,
    reset,
    isAuthTemplate,
  } = useCreateTemplate();

  const { mediaType, mediaPreview } = useSelector((state) => state.media);
  const aiGeneratedData = useSelector((state) => state.whatsapp.aiGeneration?.data);

  // Navigate away on successful template creation
  useEffect(() => {
    if (success) navigate('/whatsapp/templates', { replace: true });
  }, [success, navigate]);

  // When AI generates data, populate the form fields
  useEffect(() => {
    if (!aiGeneratedData) return;

    const tmpl = aiGeneratedData?.generatedTemplate || aiGeneratedData;

    if (tmpl) {
      const headerComp = tmpl.components?.find(c => c.type === 'HEADER');
      const bodyComp = tmpl.components?.find(c => c.type === 'BODY');

      setMultipleFormData({
        name: tmpl.name || '',
        category: (tmpl.category || 'utility').toLowerCase(),
        language: tmpl.language || 'en',
        bodyText: bodyComp?.text || '',
        headerText: headerComp?.text || '',
      });
    }
  }, [aiGeneratedData]);

  // Clean up AI generation state on unmount
  useEffect(() => {
    return () => {
      dispatch(resetAIGeneration());
    };
  }, [dispatch]);

  const handleBackOrDiscard = () => {
    if (typeof reset === 'function') reset();
    dispatch(clearMedia());
    dispatch(resetWhatsappState());
    navigate('/whatsapp/templates');
  };

  return (
    <div className={waShell(whatsappPageShell.hybrid, "animate-fade-in")}>
      <TemplatePageHeader onSubmit={submit} loading={loading} onBack={handleBackOrDiscard} onDiscard={handleBackOrDiscard} />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 animate-fade-in-up">
        <div className="lg:col-span-7 space-y-6">
          {showAITemplateGenerator && (
            <AIGeneratorCard />
          )}
          <TemplateDetailsForm formData={formData} setFormData={setFormData} validationErrors={validationErrors} />
 
          {isAuthTemplate ? (
            <AuthenticationTemplateForm formData={formData} setFormData={setFormData} validationErrors={validationErrors} />
          ) : (
            <RegularTemplateForm
              formData={formData}
              setFormData={setFormData}
              validationErrors={validationErrors}
              headerVars={headerVars}
              bodyVars={bodyVars}
              sampleValues={sampleValues}
              sampleErrors={sampleErrors}
              handleSampleChange={handleSampleChange}
              sampleValidationMessage={sampleValidationMessage}
              localHeaderError={localHeaderError}
              localBodyError={localBodyError}
              handleHeaderTextChange={handleHeaderTextChange}
            />
          )}
        </div>

        <div className="lg:col-span-5">
          <TemplatePreview
            data={{
              ...formData,
              sampleValues,
              mediaType,
              mediaPreview,
              isAuthTemplate,
            }}
            mode="create"
          />
        </div>
      </div>
    </div>
  );
}
